import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box,
    Typography,
    Chip,
    TablePagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import { formatDate } from "../../utils/dateUtils";
import CustomButton from "../../components/common/CustomButton";
import CustomHeader from "../../components/common/CustomHeader";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomFilter from "../../components/common/CustomFilter";

import CustomTable from "../../components/common/CustomTable";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";

import { interviewApi } from "../../api/interviewApi";

export const jobFilters = [
    {
        key: "status",
        label: "Status",
        type: "checkbox",
        options: [
            { label: "Open", value: "open" },
            { label: "Closed", value: "closed" },
        ],
    }
];


// in fetchJobs and handleApply, after setJobs(formatted):


const ReportsOverview = () => {

    const theme = useTheme();

    const STATUS_COLORS = {
        Open: { bg: theme.palette.primary.main, text: "#fff" },
        Closed: { bg: theme.palette.secondary.main, text: "#003040" },
    };

    const [filters, setFilters] = useState([]);
    const [searchValue, setSearchValue] = useState("");

    // Convert array → UI object
    const values = useMemo(() => {
        const obj = {};

        filters.forEach(({ property, value, operator }) => {
            // RANGE (flatten it)
            if (operator) {
                obj[property] = {
                    value: value?.value ?? value, // 👈 FIX
                    operator,
                };
            }

            // MULTI
            else if (Array.isArray(obj[property])) {
                obj[property].push(value);
            }

            // FIRST TIME
            else if (!obj[property]) {
                obj[property] = [value];
            }

            // SAFETY
            else {
                obj[property] = [obj[property], value];
            }
        });

        return obj;
    }, [filters]);

    // FINAL handler
    const handleChange = (key, value, mode, operator = "equal") => {
        setFilters((prev) => {
            // SINGLE (radio, select, range)
            if (mode === "single") {
                const filtered = prev.filter((f) => f.property !== key);

                return [
                    ...filtered,
                    {
                        property: key,
                        value,
                        operator, // important
                    },
                ];
            }

            // MULTI (checkbox)
            const exists = prev.some(
                (f) => f.property === key && f.value === value
            );

            if (exists) {
                return prev.filter(
                    (f) => !(f.property === key && f.value === value)
                );
            }

            return [
                ...prev,
                {
                    property: key,
                    value,
                },
            ];
        });
    };

    const navigate = useNavigate();

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const allJobsRef = useRef([]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        type: "",
    });

    const showSnackbar = (message, type) =>
        setSnackbar({ open: true, message, type });

    /* ---------------- Fetch Jobs ---------------- */

    const buildFilterPayload = (filters) => {
        const grouped = {};

        filters.forEach(({ property, value, operator }) => {
            const actualValue = value?.value ?? value;

            if (!grouped[property]) {
                grouped[property] = {
                    property,
                    value: [],
                    ...(operator && { operator }),
                };
            }

            grouped[property].value.push(actualValue);
        });

        return Object.values(grouped).map((item) => ({
            ...item,
            value:
                item.value.length === 1
                    ? item.value[0]
                    : item.value,
        }));
    };


    const fetchJobs = async () => {
        try {
            setLoading(true);

            const { status, data, message } =
                await interviewApi.jdWiseInterviewReport({ page: page + 1, page_size: rowsPerPage, });

            if (status !== 200 || !data) {
                showSnackbar(message || "Failed to load reports", "error");
                setJobs([]);
                return;
            }

            const formatted = Array.isArray(data?.results)
                ? data.results
                : [];

            setJobs(formatted);

            setTotal(data?.pagination?.total || 0);
            setTotalPages(data?.pagination?.total_pages || 1);
            allJobsRef.current = formatted;
        } catch (err) {
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleApply = async () => {
        setLoading(true);
        try {
            const payload = buildFilterPayload(filters)
            // console.log(payload)
            const { status, data, message } =
                await interviewApi.jdWiseInterviewReportFilter({ "filters": payload }, page + 1, rowsPerPage);

            if (status !== 200 || !data) {
                showSnackbar(message || "Failed to load reports", "error");
                setJobs([]);
                return;
            }

            const formatted = Array.isArray(data)
                ? data
                : Object.entries(data).map(([id, job]) => ({
                    id,
                    ...job,
                }));

            setJobs(formatted);
            allJobsRef.current = formatted;
        } catch (err) {
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
        }

    };



    const handleSearch = (value) => {
        setSearchValue(value);

        if (!value) {
            setJobs(allJobsRef.current);
            return;
        }

        const lower = value.toLowerCase();

        const filtered = allJobsRef.current.filter((item) =>
            item.jd_title?.toLowerCase().includes(lower)
        );

        setJobs(filtered);
    };

    const handleClear = async () => {
        setFilters([]);
        setPage(0);
        await fetchJobs()
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    /* ---------------- Pagination ---------------- */

    // const paginatedJobs = useMemo(() => {
    //     return jobs.slice(
    //         page * rowsPerPage,
    //         page * rowsPerPage + rowsPerPage
    //     );
    // }, [jobs, page, rowsPerPage]);

    /* ---------------- JD Download ---------------- */

    // const handleViewJD = async (jdId) => {
    //     try {
    //         const { status, data } = await jdApi.viewJD({
    //             jd_id: jdId,
    //         });

    //         if (status !== 200 || !data?.download_url) {
    //             showSnackbar("Unable to download JD", "error");
    //             return;
    //         }

    //         const link = document.createElement("a");
    //         link.href = data.download_url;
    //         link.download = data.jd_name || "jd";
    //         document.body.appendChild(link);
    //         link.click();
    //         document.body.removeChild(link);
    //     } catch {
    //         showSnackbar("Download failed", "error");
    //     }
    // };

    /* ---------------- Columns Config ---------------- */

    const columns = [
        {
            label: "Job Title",
            field: "jd_title",
            sx: { fontWeight: 600 },
        },
        {
            label: "Posted",
            field: "postedDate",
            render: (val) =>
                formatDate(val)
        },
        // {
        //     label: "Status",
        //     field: "status",
        //     render: (val) => (
        //         <Chip
        //             size="small"
        //             label={val}
        //             sx={{
        //                 fontWeight: 600,
        //                 backgroundColor: STATUS_COLORS[val]?.bg,
        //                 color: STATUS_COLORS[val]?.text,
        //             }}
        //         />
        //     ),
        // },
        // {
        //     label: "Candidates",
        //     field: "candidates",
        //     sx: { textAlign: "center" },
        // },
        // {
        //     label: "Mode",
        //     field: "mode",
        //     sx: { textAlign: "center" }
        // },
        {
            label: "Action",
            field: "id",
            sx: {
                textAlign: "center",
                width: 220,
            },
            headerSx: {
                textAlign: "center",
            },
            render: (_, row) => (
                <CustomButton
                    size="small"
                    variant="contained"
                    onClick={() => {
                        navigate("/reports/candidates", {
                            state: {
                                jd_id: row.jd_id,
                                mode: row.mode,
                            },
                        });
                    }}
                >
                    Details
                </CustomButton>
            )
        },
    ];

    /* ---------------- UI ---------------- */

    return (
        <Box sx={{ minHeight: "100vh" }}>
            <CustomHeader
                title="Interview Reports"
                subtitle="Detailed metrics and candidate statuses across job listings."
                icon={<AssessmentOutlinedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />}
                breadcrumbs={[
                    { label: "Dashboard", path: "/recruiter/dashboard" },
                    { label: "Reports", path: "/reports" }, // The current page entered
                ]}
            />
            <Box
                sx={{
                    p: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2, // consistent spacing between all sections
                }}
            >
                {/* Filter Section */}
                <CustomFilter
                    filtersConfig={jobFilters}
                    values={values}
                    onChange={handleChange}
                    onApply={handleApply}
                    onClear={handleClear}
                    onSearch={handleSearch}
                    searchValue={searchValue}
                />

                {/* Content Section */}
                {loading ? (
                    <SkeletonLoader type="table" rows={5} columns={columns} />
                ) : jobs.length === 0 ? (
                    <Typography sx={{ textAlign: "center", py: 6 }}>
                        No interview reports available.
                    </Typography>
                ) : (
                    <>
                        <CustomTable
                            columns={columns}
                            rows={jobs}
                            tableSx={{}}
                            headerCellSx={{
                                backgroundColor: "#f8fafc",
                                fontSize: "0.85rem",
                            }}
                        />

                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[5, 10, 25]}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(+e.target.value);
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Box>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={() =>
                    setSnackbar((s) => ({ ...s, open: false }))
                }
            />
        </Box>
    );
};

export default ReportsOverview;
